import { interpolationAtTime, type LayerState, type LayerFeature, type CometProps } from '@vitals/shared';
import { pathCumulative, pointAtDistance, projectDistance, stepAlongPath } from './alongPath.js';
import { STALL_RESUME_MS } from './animationStall.js';
import { isResumeFrame } from './pageResume.js';

const DEFAULT_CATCHUP_MPS = 20;
const asProps = (f: LayerFeature) => f.props as unknown as CometProps;

export interface CometSample {
  id: string;
  position: [number, number];
  props: CometProps;
}
export interface CometMotion {
  step(state: LayerState, nowSec: number): CometSample[];
}

interface TrainDisplay { coords: [number, number][]; cum: number[]; dist: number; }

/** Along-path distance (m) a train should occupy at absolute time `t`. */
export function distanceAtTime(cum: number[], timestamps: number[], t: number): number {
  const interpolation = interpolationAtTime(cum.length, timestamps, t);
  if (!interpolation) return 0;
  const { from, to, fraction } = interpolation;
  return cum[from]! + (cum[to]! - cum[from]!) * fraction;
}

/** Each instance owns isolated frame state so multiple comet layers never
 * corrupt one another's `lastFrameMs`/displayed positions.
 *
 * `catchupMps` caps how fast a marker closes on a revised path position —
 * trains absorb revisions at 20 m/s; slower vehicles (ferries) want less.
 */
export function createCometMotion(opts?: { catchupMps?: number }): CometMotion {
  const catchupMps = opts?.catchupMps ?? DEFAULT_CATCHUP_MPS;
  let displayed = new Map<string, TrainDisplay>();
  let lastFrameMs = -1;

  return {
    step(state: LayerState, nowSec: number): CometSample[] {
      const t = typeof performance !== 'undefined' ? performance.now() : 0;
      const rawDt = lastFrameMs >= 0 ? (t - lastFrameMs) / 1000 : 0;
      const stalled = rawDt > STALL_RESUME_MS / 1000 || isResumeFrame();
      const dt = lastFrameMs >= 0 ? Math.min(0.1, rawDt) : 0;
      lastFrameMs = t;
      const maxStep = catchupMps * dt;

      const next = new Map<string, TrainDisplay>();
      const samples = state.features.map((f): CometSample => {
        const p = asProps(f);
        const path = (f.geometry as { coordinates: [number, number][] }).coordinates;
        const prev = displayed.get(f.id);
        let cum: number[];
        let prevDist: number | undefined;
        if (prev && prev.coords === path) {
          cum = prev.cum;
          prevDist = prev.dist;
        } else {
          cum = pathCumulative(path);
          prevDist = prev
            ? projectDistance(path, cum, pointAtDistance(prev.coords, prev.cum, prev.dist))
            : undefined;
        }
        const targetDist = distanceAtTime(cum, p.timestamps, nowSec);
        const dist = stalled ? targetDist : stepAlongPath(prevDist, targetDist, maxStep);
        const position = pointAtDistance(path, cum, dist);
        next.set(f.id, { coords: path, cum, dist });
        return { id: f.id, position, props: p };
      });
      displayed = next;
      return samples;
    },
  };
}
